// src/features/auth/components/VerificationForm.tsx
import React, { useState, useRef, useEffect } from 'react';
import { ShieldCheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface VerificationFormProps {
  onSubmit: (code: string) => void;
  loading?: boolean;
  retryCount?: number;
  rateLimitCountdown?: number;
}

const VerificationForm: React.FC<VerificationFormProps> = ({
  onSubmit,
  loading = false,
  retryCount = 0,
  rateLimitCountdown = 0
}) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Clear form when there's an error (for retry)
  useEffect(() => {
    if (retryCount > 0) {
      setCode(['', '', '', '', '', '']);
      setError(null);
      inputRefs.current[0]?.focus();
    }
  }, [retryCount]);

  const handleInputChange = (index: number, value: string) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1); // Only take the last character
    setCode(newCode);
    setError(null);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (newCode.every(digit => digit !== '') && !loading) {
      handleSubmit(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === 'Enter') {
      handleSubmit(code.join(''));
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      setError(null);
      
      // Focus last input
      inputRefs.current[5]?.focus();
      
      // Auto-submit
      if (!loading) {
        handleSubmit(pastedData);
      }
    }
  };

  const handleSubmit = (codeString: string) => {
    if (codeString.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    if (rateLimitCountdown > 0) {
      setError(`Please wait ${rateLimitCountdown} seconds before trying again`);
      return;
    }

    onSubmit(codeString);
  };

  const handleManualSubmit = () => {
    handleSubmit(code.join(''));
  };

  const isComplete = code.every(digit => digit !== '');
  const isDisabled = loading || rateLimitCountdown > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <ShieldCheckIcon className="h-12 w-12 text-primary-light dark:text-primary-dark mx-auto mb-3" />
        <h4 className="text-lg font-medium text-theme-text dark:text-dark-theme-text mb-2">
          Enter Verification Code
        </h4>
        <p className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">
          Open your authenticator app and enter the 6-digit code for your account
        </p>
      </div>

      {/* Code Input */}
      <div className="space-y-4">
        <div className="flex justify-center space-x-2">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={el => { inputRefs.current[index] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={e => handleInputChange(index, e.target.value)}
              onKeyDown={e => handleKeyDown(index, e)}
              onPaste={index === 0 ? handlePaste : undefined}
              disabled={isDisabled}
              className={`w-12 h-12 text-center text-lg font-mono border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-light dark:focus:ring-primary-dark transition-colors duration-150 ${
                isDisabled
                  ? 'bg-neutral-100 dark:bg-neutral-800 border-neutral-300 dark:border-neutral-600 text-neutral-500 dark:text-neutral-400'
                  : 'bg-theme-bg dark:bg-dark-theme-bg border-theme-border dark:border-dark-theme-border text-theme-text dark:text-dark-theme-text hover:border-primary-light dark:hover:border-primary-dark'
              }`}
            />
          ))}
        </div>

        {/* Error Display */}
        {error && (
          <div className="flex items-center justify-center space-x-2 text-red-600 dark:text-red-400">
            <ExclamationTriangleIcon className="h-4 w-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Rate Limit Display */}
        {rateLimitCountdown > 0 && (
          <div className="text-center">
            <p className="text-sm text-orange-600 dark:text-orange-400">
              Too many attempts. Please wait {rateLimitCountdown} seconds before trying again.
            </p>
          </div>
        )}

        {/* Retry Count */}
        {retryCount > 0 && retryCount < 3 && (
          <div className="text-center">
            <p className="text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary">
              Attempt {retryCount + 1} of 3
            </p>
          </div>
        )}
      </div>

      {/* Manual Submit Button */}
      {isComplete && !loading && (
        <div className="flex justify-center">
          <button
            onClick={handleManualSubmit}
            disabled={isDisabled}
            className="px-6 py-2 bg-gradient-button text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light dark:focus:ring-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-150"
          >
            Verify Code
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-light dark:border-primary-dark"></div>
          <span className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">
            Verifying code...
          </span>
        </div>
      )}

      {/* Help Text */}
      <div className="bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary rounded-md p-4">
        <h5 className="text-sm font-medium text-theme-text dark:text-dark-theme-text mb-2">
          Having trouble?
        </h5>
        <ul className="text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary space-y-1">
          <li>• Make sure your device's time is correct</li>
          <li>• The code refreshes every 30 seconds</li>
          <li>• Try waiting for a new code if the current one doesn't work</li>
          <li>• Check that you're looking at the correct account in your app</li>
        </ul>
      </div>
    </div>
  );
};

export default VerificationForm;
